import React, { useMemo, useRef, useEffect } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { useTexture, Stars, OrbitControls } from "@react-three/drei";
import { useXR } from "@react-three/xr";
import * as THREE from "three";
import { GOLD_VEC, latLonToUV, latLonToVec3, CITIES } from "../lib/storm";
import { solarDir } from "../lib/environment";
import { PROFILES } from "../lib/quality";
import {
  cloudVert, cloudFrag, atmoVert, atmoFrag, oceanVert, oceanFrag,
} from "../lib/shaders";
import WindField from "./WindField";
import StormNodes from "./StormNodes";

const R = 0.85;

export default function GlobeScene({ sim, activeNode, stormPos, eventTrack, event, onSelectCity, preset, cycle, viewMode, framing = "immersive", isPortrait = false, globeScale = 1, enableDive = false, onZoomDive, onDiveLocation, autoFollow = false, followLatLon, focusLatLon, timeline = 0, profile, reducedMotion = false }) {
  const { camera, gl, size } = useThree();
  const inVR = useXR((s) => !!s.session);
  const p = inVR ? PROFILES.vr : (profile || PROFILES.desktop);
  const [dayMap, specMap, cloudTex, lightsMap, normalMap] = useTexture([
    "/textures/earth_day.webp",
    "/textures/earth_spec.jpg",
    "/textures/earth_clouds.jpg",
    "/textures/earth_lights.jpg",
    "/textures/earth_normal.jpg",
  ]);

  useEffect(() => {
    const maxAniso = gl.capabilities.getMaxAnisotropy();
    [dayMap, cloudTex, lightsMap].forEach((t) => { t.colorSpace = THREE.SRGBColorSpace; });
    [dayMap, specMap, cloudTex, lightsMap, normalMap].forEach((t) => {
      t.anisotropy = maxAniso;
      t.generateMipmaps = true;
      t.minFilter = THREE.LinearMipmapLinearFilter;
      t.needsUpdate = true;
    });
  }, [dayMap, specMap, cloudTex, lightsMap, normalMap, gl]);

  const spinGroup = useRef();
  const cloudMat = useRef();
  const atmoMat = useRef();
  const oceanMat = useRef();
  const sunRef = useRef();
  const ambientRef = useRef();
  const controlsRef = useRef();
  const driftRef = useRef();
  const diveGuard = useRef(false);

  // View-offset framing: keep the globe centre-right (landing) or centred (immersive)
  // without moving the mesh, so orbit spins the world in place.
  useEffect(() => {
    if (framing === "landing") {
      if (isPortrait) camera.setViewOffset(size.width, size.height, 0, size.height * 0.24, size.width, size.height);
      else camera.clearViewOffset();
    } else {
      camera.clearViewOffset();
    }
    return () => camera.clearViewOffset();
  }, [framing, isPortrait, size.width, size.height, camera]);

  // Reframe camera + orbit target when switching between landing and immersive.
  useEffect(() => {
    diveGuard.current = false;
    const c = controlsRef.current;
    if (!c) return;
    c.target.set(0, 1.2, -1.5);
    camera.position.set(0, 1.2, framing === "landing" ? 1.9 : 2.2);
    c.update();
  }, [framing, camera]);

  const cloudUniforms = useMemo(() => ({
    uTime: { value: 0 },
    uIntensity: { value: 0.6 },
    uStormUV: { value: new THREE.Vector2(0.4, 0.6) },
    uActive: { value: 1 },
    uCycle: { value: 0 },
    uMode: { value: 0 },
    uSunDir: { value: new THREE.Vector3(1, 0.3, 0.6).normalize() },
    uCloudTex: { value: cloudTex },
    uGold: { value: GOLD_VEC },
  }), [cloudTex]);

  const atmoUniforms = useMemo(() => ({
    uInner: { value: new THREE.Color("#2a6cff") },
    uOuter: { value: GOLD_VEC.clone() },
    uSunDir: { value: new THREE.Vector3(1, 0.3, 0.6).normalize() },
    uIntensity: { value: 0.6 },
  }), []);

  const oceanUniforms = useMemo(() => ({
    uTime: { value: 0 },
    uIntensity: { value: 0.6 },
    uZoom: { value: 0 },
    uMask: { value: specMap },
    uColor: { value: new THREE.Color("#1f6feb") },
    uMode: { value: 0 },
    uCycle: { value: 0 },
  }), [specMap]);

  const globeCenter = useMemo(() => new THREE.Vector3(0, 1.2, -1.5), []);

  // preset / cycle driven colors
  const sunDirection = useMemo(() => solarDir(preset.elevation, preset.azimuth), [preset]);
  const baseDirColor = useMemo(() => new THREE.Color(preset.dirColor).lerp(new THREE.Color(cycle.tintLight), 0.5), [preset, cycle]);
  const ambientColor = useMemo(() => new THREE.Color(preset.ambientColor).lerp(new THREE.Color(cycle.tintAmbient), 0.5), [preset, cycle]);
  const greyColor = useMemo(() => new THREE.Color("#9aa0ae"), []);

  useEffect(() => {
    if (atmoMat.current) {
      atmoMat.current.uniforms.uInner.value.set(cycle.atmoInner).lerp(new THREE.Color(preset.atmoInner), 0.5);
      atmoMat.current.uniforms.uOuter.value.set(cycle.atmoOuter).lerp(new THREE.Color(preset.atmoOuter), 0.5);
    }
    if (cloudMat.current) cloudMat.current.uniforms.uCycle.value = cycle.cloudCycle;
    if (oceanMat.current) oceanMat.current.uniforms.uCycle.value = cycle.cloudCycle;
  }, [cycle, preset]);

  useEffect(() => {
    const m = viewMode === "thermal" ? 2 : (viewMode === "forensic" ? 1 : 0);
    if (cloudMat.current) cloudMat.current.uniforms.uMode.value = m;
    if (oceanMat.current) oceanMat.current.uniforms.uMode.value = m;
  }, [viewMode]);

  useFrame((state, dt) => {
    const t = state.clock.elapsedTime;
    const intensity = sim.current.intensity / 5;

    if (cloudMat.current) {
      cloudMat.current.uniforms.uTime.value = t;
      cloudMat.current.uniforms.uIntensity.value = intensity;
      cloudMat.current.uniforms.uSunDir.value.copy(sunDirection);
      const active = activeNode ? stormPos[activeNode] : stormPos.hurricane_polo;
      if (active) {
        const uv = latLonToUV(active.lat, active.lon);
        cloudMat.current.uniforms.uStormUV.value.set(uv.x, uv.y);
      }
    }
    if (oceanMat.current) {
      oceanMat.current.uniforms.uTime.value = t;
      oceanMat.current.uniforms.uIntensity.value = intensity;
      const dist = camera.position.distanceTo(globeCenter);
      oceanMat.current.uniforms.uZoom.value = THREE.MathUtils.clamp((2.6 - dist) / (2.6 - 1.25), 0, 1);
    }

    // Diffused sun: intensity drops + colour greys toward Cat 5 (faint glow through clouds)
    if (sunRef.current) {
      sunRef.current.position.copy(sunDirection).multiplyScalar(6);
      sunRef.current.position.y += 1.0;
      sunRef.current.intensity = preset.dirIntensity * (1 - 0.7 * intensity);
      sunRef.current.color.copy(baseDirColor).lerp(greyColor, intensity * 0.8);
    }
    if (ambientRef.current) {
      ambientRef.current.intensity = preset.ambient * (1 - 0.15 * intensity);
      ambientRef.current.color.copy(ambientColor);
    }
    if (atmoMat.current) {
      atmoMat.current.uniforms.uIntensity.value = intensity;
      atmoMat.current.uniforms.uSunDir.value.copy(sunDirection);
    }

    // idle globe rotation — or Auto-Follow / focus yaw to keep a target storm centred
    if (spinGroup.current) {
      const follow = focusLatLon || (autoFollow ? followLatLon : null);
      if (follow) {
        spinGroup.current.updateWorldMatrix(true, false);
        const loc = latLonToVec3(follow.lat, follow.lon, R).applyMatrix4(spinGroup.current.matrixWorld).sub(globeCenter);
        const camDir = camera.position.clone().sub(globeCenter);
        let d = Math.atan2(camDir.x, camDir.z) - Math.atan2(loc.x, loc.z);
        while (d > Math.PI) d -= 2 * Math.PI;
        while (d < -Math.PI) d += 2 * Math.PI;
        spinGroup.current.rotation.y += d * Math.min(1, dt * (focusLatLon ? 3.5 : 2.5));
      } else {
        spinGroup.current.rotation.y += 0.02 * dt;
      }
    }

    // Cinematic parallax drift on the landing page (subtle float)
    if (driftRef.current) {
      if (framing === "landing" && !reducedMotion) {
        driftRef.current.position.x = Math.sin(t * 0.22) * 0.05;
        driftRef.current.position.y = 1.2 + Math.cos(t * 0.17) * 0.04;
      } else {
        driftRef.current.position.set(0, 1.2, -1.5);
      }
    }

    // Zoom-Dive trigger: zooming close to a City Anchor dives into its 3D scene
    if (enableDive && onZoomDive && !diveGuard.current && spinGroup.current) {
      const camDist = camera.position.distanceTo(globeCenter);
      if (camDist < 1.3) {
        spinGroup.current.updateWorldMatrix(true, false);
        let best = null, bestD = Infinity;
        for (const city of CITIES) {
          const wp = latLonToVec3(city.lat, city.lon, R * 1.05).applyMatrix4(spinGroup.current.matrixWorld);
          const d = camera.position.distanceTo(wp);
          if (d < bestD) { bestD = d; best = city; }
        }
        if (best) { diveGuard.current = true; onZoomDive(best); }
      }
    }
  });

  return (
    <group>
      <OrbitControls
        ref={controlsRef}
        makeDefault
        enableRotate
        enableZoom
        target={[0, 1.2, -1.5]}
        enablePan={false}
        enableDamping
        dampingFactor={0.05}
        rotateSpeed={0.55}
        zoomSpeed={0.9}
        minDistance={0.9}
        maxDistance={6}
      />

      <Stars radius={60} depth={40} count={Math.round((preset.night ? 1.7 : 1) * p.stars)} factor={preset.night ? 5 : 3} saturation={0} fade speed={0.5} />

      {/* Night moon: cool glowing disc + halo + faint moonlight */}
      {preset.night && (
        <group position={[-3.4, 3.3, -2.6]}>
          <mesh><sphereGeometry args={[0.34, 32, 32]} /><meshBasicMaterial color="#eaf0ff" toneMapped={false} /></mesh>
          <mesh><sphereGeometry args={[0.62, 32, 32]} /><meshBasicMaterial color="#9fb6ff" transparent opacity={0.24} depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false} /></mesh>
          <mesh><sphereGeometry args={[1.15, 32, 32]} /><meshBasicMaterial color="#6f8bff" transparent opacity={0.1} depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false} /></mesh>
          <pointLight intensity={0.55} distance={28} color="#bcd0ff" />
        </group>
      )}

      <directionalLight ref={sunRef} position={[5, 2.2, 3]} intensity={1.0} color="#fff6e0" />
      <ambientLight ref={ambientRef} intensity={0.7} color="#aebdd6" />
      <pointLight position={[-3, -1, -2]} intensity={0.4} color="#3a6cff" />

      <group ref={driftRef} position={[0, 1.2, -1.5]}>
        <group ref={spinGroup} rotation={[0.2, 0, 0]} scale={globeScale}>
          {/* Layer 1: solid core with topographic normal + night lights */}
          <mesh data-testid="earth-core" castShadow={false} receiveShadow={false}
            onClick={(e) => {
              if (!onDiveLocation || !spinGroup.current) return;
              if (e.delta > 6) return;  // ignore orbit drags
              e.stopPropagation();
              const local = spinGroup.current.worldToLocal(e.point.clone());
              const rr = local.length() || 1;
              const lat = 90 - Math.acos(THREE.MathUtils.clamp(local.y / rr, -1, 1)) * 180 / Math.PI;
              let lon = Math.atan2(local.z, -local.x) * 180 / Math.PI - 180;
              while (lon < -180) lon += 360; while (lon > 180) lon -= 360;
              onDiveLocation(lat, lon);
            }}
            onPointerOver={() => { document.body.style.cursor = "pointer"; }}
            onPointerOut={() => { document.body.style.cursor = "auto"; }}
          >
            <sphereGeometry args={[R, p.sphere, p.sphere]} />
            <meshStandardMaterial
              map={dayMap}
              normalMap={normalMap}
              normalScale={new THREE.Vector2(1.3, 1.3)}
              roughnessMap={specMap}
              roughness={0.9}
              metalness={0.05}
              emissiveMap={lightsMap}
              emissive={new THREE.Color("#ffd27f")}
              emissiveIntensity={preset.cityLights ? 1.0 : 0.45}
            />
          </mesh>

          {/* Ocean wave-height shell */}
          <mesh>
            <sphereGeometry args={[R * 1.004, p.ocean, p.ocean]} />
            <shaderMaterial ref={oceanMat} vertexShader={oceanVert} fragmentShader={oceanFrag} uniforms={oceanUniforms} transparent depthWrite={false} />
          </mesh>

          {/* Layer 2: dynamic cloud deck / eyewall */}
          <mesh>
            <sphereGeometry args={[R * 1.04, p.cloud, p.cloud]} />
            <shaderMaterial ref={cloudMat} vertexShader={cloudVert} fragmentShader={cloudFrag} uniforms={cloudUniforms} transparent depthWrite={false} />
          </mesh>

          {/* Wind vector field (cyan flow-lines, hugging the cloud deck) */}
          <WindField sim={sim} rMin={R * 1.045} rMax={R * 1.085} diagonal={cycle.windMode === "diagonal"} />

          {/* Storm nodes, city anchors, historical track */}
          <StormNodes sim={sim} radius={R * 1.041} activeNode={activeNode} stormPos={stormPos} eventTrack={eventTrack} event={event} onSelectCity={onSelectCity} viewMode={viewMode} timeline={timeline} />

          {/* Layer 3: Rayleigh atmosphere fresnel rim */}
          <mesh>
            <sphereGeometry args={[R * 1.17, 64, 64]} />
            <shaderMaterial ref={atmoMat} vertexShader={atmoVert} fragmentShader={atmoFrag} uniforms={atmoUniforms} transparent depthWrite={false} blending={THREE.AdditiveBlending} side={THREE.FrontSide} />
          </mesh>
        </group>
      </group>
    </group>
  );
}
